
import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import Index from "./pages/Index";
import StartPage from "./pages/start/StartPage";
import SignUp, { SIGNUP_COMPLETION_PATH } from "./pages/SignUp";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import IndividualOnboardingPage from "./pages/onboarding/IndividualOnboardingPage";
import InstitutionOnboardingPage from "./pages/onboarding/InstitutionOnboardingPage";
import NotFound from "./pages/NotFound";
import DashboardLayout from "./layouts/DashboardLayout";
import DashboardHome from "./pages/dashboard/DashboardHome";
import NotificationsPage from "./pages/dashboard/NotificationsPage";
import ClientsListPage from "./clients/list/pages/ClientsListPage";
import MealPlanningOptionsPage from "./pages/meal-plan/MealPlanningOptionsPage";
import MealPlanningClientMealPlanPage from "./pages/meal-plan/MealPlanningClientMealPlanPage";
import MealPlanningGenericMealPlanPage from "./pages/meal-plan/MealPlanningGenericMealPlanPage";
import GenericMealPlansPage from "./pages/meal-plan/GenericMealPlansPage";
import { GenericMealPlanNutritionRoutePage } from "./pages/meal-plan/GenericMealPlanNutritionPage";
import TemplatesPage from "./pages/dashboard/TemplatesPage";
import InvoicingPage from "./pages/dashboard/InvoicingPage";
import KnowledgebasePage from "./pages/dashboard/KnowledgebasePage";
import MyProfilePage from "./pages/dashboard/MyProfilePage";
import AppointmentsPage from "./pages/appointments/AppointmentsPage";
import AppointmentsCalendarViewPage from "./pages/appointments/AppointmentsCalendarViewPage";
import AvailabilityOnboardingGate from "@/components/appointments/AvailabilityOnboardingGate";
import QuestionnairesFinalPage from "./pages/dashboard/QuestionnairesFinalPage";
import QuestionnaireFinalDetailPage from "./pages/dashboard/QuestionnaireFinalDetailPage";
import QuestionnaireAssignmentPage from "./pages/dashboard/QuestionnaireAssignmentPage";
import QuestionnaireResponsesPage from "./pages/dashboard/QuestionnaireResponsesPage";
import QuestionnaireResponsePreviewPage from "./pages/dashboard/QuestionnaireResponsePreviewPage";
import ClientDetailsPage from "./clients/details/legacy/pages/ClientDetailsPage";
import ClientDetailsV2Page from "./clients/details/v2/pages/ClientDetailsV2Page";
import AddClientPage from "./clients/onboarding/pages/AddClientPage";
import ManualMealPlanDraftPage from "./pages/manual/ManualMealPlanDraftPage";
import CreateManualMealPlanPage from "./pages/manual/CreateManualMealPlanPage";
import CreateAIMealPlanPage from "./pages/ai/CreateAIMealPlanPage";
import CreateAutoMealPlanPage from "./pages/auto/CreateAutoMealPlanPage";
import AIMealPlanDraftPage from "./pages/ai/AIMealPlanDraftPage";
import AIMealPlanRecipeDetailsPage from "./pages/ai/AIMealPlanRecipeDetailsPage";
import AutomatedMealDraftPage from "./pages/auto/AutomatedMealDraftPage";
import FinalizedMealPlanPage from "./pages/finalized/FinalizedMealPlanPage";
import CreateCustomRecipePage from "./pages/recipes/CreateCustomRecipePage";
import EditCustomRecipePage from "./pages/recipes/EditCustomRecipePage";
import NutritionalTargetsPage from "./pages/clients/NutritionalTargetsPage";
import NutritionalAnalysisPage from "./pages/manual/NutritionalAnalysisPage";
import RecipesPage from "./pages/recipes/RecipesPage";
import RecipeDetailsPage from "./pages/recipes/RecipeDetailsPage";
import IngredientsPage from "./pages/ingredients/IngredientsPage";
import FeedbackPage from "./pages/feedback/FeedbackPage";
import UserProvisioningPage from "./pages/admin/UserProvisioningPage";
import CustomRecipeReviewPage from "./pages/admin/CustomRecipeReviewPage";
import NutritionistFeedbackPage from "./pages/admin/NutritionistFeedbackPage";
import UsageQualityMetricsPage from "./pages/admin/UsageQualityMetricsPage";
import LandingOperationsPage from "./pages/admin/LandingOperationsPage";
import AIRecipeReviewPage from "./pages/admin/AIRecipeReviewPage";
import CallbackPage from "./pages/auth/CallbackPage";
import ConsentRequiredPage from "./pages/auth/ConsentRequiredPage";
import SubscriptionRequiredLegacyRedirect from "./pages/auth/SubscriptionRequiredLegacyRedirect";
import OnboardingWelcomePage from "./pages/auth/OnboardingWelcomePage";
import BlogPostPage from "./pages/blog/BlogPostPage";
import CompanionAppPage from "./pages/companion/CompanionAppPage";
import BiomarkerNutritionPage from "./pages/platform/BiomarkerNutritionPage";
import PersonalisedNutritionPage from "./pages/platform/PersonalisedNutritionPage";
import LegalDocumentPage from "./pages/legal/LegalDocumentPage";
import { legalDocuments } from "./pages/legal/legalDocuments";
import { AuthGuard } from "@/components/auth/AuthGuard";
import { DashboardAccessGuard } from "@/components/auth/DashboardAccessGuard";
import { SubscriptionGateBridge } from "@/components/auth/SubscriptionGateBridge";
import { FounderAgreementGateBridge } from "@/components/auth/FounderAgreementGateBridge";
import CookieConsentManager from "@/components/cookies/CookieConsentManager";
import AttributionTracker from "@/components/AttributionTracker";
import { DemoModeProvider } from "@/demo/DemoModeProvider";
import PublicQuestionnairePage from "./pages/public/PublicQuestionnairePage";
import PublicBookingPage from "./pages/public/PublicBookingPage";
import PublicReschedulePage from "./pages/public/PublicReschedulePage";
import ClientConsentPage from "./pages/public/ClientConsentPage";
import { consumeOAuthHashSession, getCurrentUser, updateConsentStatus } from "@/lib/api/auth";
import { getDefaultAuthenticatedRoute } from "@/lib/accessControl";
import { trackAnalyticsPageView } from "@/lib/analytics";
import {
  REGISTRATION_BLOCKED_MESSAGE,
  clearRegistrationBlockedFromUrl,
  readRegistrationBlockedFromUrl,
} from "@/lib/auth/registrationGate";
import { showWarningToast } from "@/utils/toast-utils";
import {
  clearStoredSignupConsent,
  getSignupConsentUpdatePayload,
  readStoredSignupConsent,
} from "@/lib/signupConsent";

const GOOGLE_SIGNUP_ONBOARDING_ROUTE = '/onboarding/practice-type/institution';

const AnalyticsPageTracker = () => {
  const location = useLocation();
  const hasTrackedInitialPage = useRef(false);

  useEffect(() => {
    if (!hasTrackedInitialPage.current) {
      hasTrackedInitialPage.current = true;
      return;
    }

    trackAnalyticsPageView();
  }, [location.pathname, location.search]);

  return null;
};

const OAuthHashSessionBridge = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let isCancelled = false;

    const syncOAuthHashSession = async () => {
      const result = await consumeOAuthHashSession();
      if (!result.handled || isCancelled) {
        if (!isCancelled && readRegistrationBlockedFromUrl()) {
          clearRegistrationBlockedFromUrl();
          showWarningToast('Sign up unavailable', REGISTRATION_BLOCKED_MESSAGE);
          navigate('/signup', { replace: true, state: { registrationBlocked: true } });
        }
        return;
      }

      if (result.registrationBlocked) {
        showWarningToast('Sign up unavailable', REGISTRATION_BLOCKED_MESSAGE);
        navigate('/signup', { replace: true, state: { registrationBlocked: true } });
        return;
      }

      if (!result.authenticated) {
        navigate('/login', { replace: true });
        return;
      }

      const currentUser = getCurrentUser();
      let consentRequired = result.consentRequired === true;
      if (consentRequired) {
        const signupConsent = readStoredSignupConsent(currentUser?.email);
        if (signupConsent) {
          try {
            const consentResponse = await updateConsentStatus(
              getSignupConsentUpdatePayload(signupConsent)
            );
            consentRequired = consentResponse.consent.accessBlocked;
            clearStoredSignupConsent(currentUser?.email ?? null);
          } catch (error) {
            console.warn('Failed to save signup consent after OAuth callback', error);
          }
        }
      } else {
        clearStoredSignupConsent(currentUser?.email ?? null);
      }

      if (consentRequired) {
        navigate('/auth/consent-required', { replace: true });
        return;
      }

      if (result.memberStatus === 'new') {
        navigate(GOOGLE_SIGNUP_ONBOARDING_ROUTE, { replace: true });
        return;
      }

      navigate(getDefaultAuthenticatedRoute(currentUser), { replace: true });
    };

    void syncOAuthHashSession();

    return () => {
      isCancelled = true;
    };
  }, [location.pathname, navigate]);

  return null;
};

const RouteScrollManager = () => {
  const location = useLocation();

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    if (location.pathname.startsWith('/blog/')) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      });
      return;
    }

    const isScrollableHashRoute = location.pathname === '/' || location.pathname === '/companion-app';
    if (!isScrollableHashRoute) {
      return;
    }

    if (!location.hash) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      });
      return;
    }

    const sectionId = decodeURIComponent(location.hash.slice(1));
    const scrollToSection = () => {
      const target = document.getElementById(sectionId);
      if (!target) {
        return false;
      }

      const navHeight = document.getElementById('site-navigation')?.offsetHeight ?? 0;
      const targetTop = target.getBoundingClientRect().top + window.scrollY - navHeight - 16;
      window.scrollTo({ top: Math.max(targetTop, 0), left: 0, behavior: 'auto' });
      return true;
    };

    requestAnimationFrame(() => {
      if (!scrollToSection()) {
        window.setTimeout(scrollToSection, 80);
      }
    });
  }, [location.pathname, location.hash]);

  return null;
};

const TemplateViewRedirect = () => {
  const { templateId = '' } = useParams<{ templateId: string }>();
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const fromParam = search.get('from')?.trim();
  const fromPath = fromParam && fromParam.length > 0 ? fromParam : '/templates';

  const nextSearch = new URLSearchParams({
    template: 'true',
    from: fromPath,
  });

  return (
    <Navigate
      to={`/meal-plans/${encodeURIComponent(templateId)}/clients/template-preview/nutrition/1?${nextSearch.toString()}`}
      replace
    />
  );
};

const MealPlanNutritionRoute = () => {
  return <NutritionalAnalysisPage />;
};

const DashboardPage = ({ children }: { children: ReactNode }) => (
  <DashboardAccessGuard>
    <DashboardLayout>
      {children}
    </DashboardLayout>
  </DashboardAccessGuard>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <Toaster />
    <Sonner />
    <CookieConsentManager />
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AnalyticsPageTracker />
      <AttributionTracker />
      <OAuthHashSessionBridge />
      <SubscriptionGateBridge />
      <FounderAgreementGateBridge />
      <DemoModeProvider />
      <RouteScrollManager />
      {/* CloudFront only serves the SPA shell for paths it knows about; everything else is a real 404.
          When adding a route, also add it to infrastructure/lib/cloudfront-functions/frontend-viewer-request.js
          in caloriescience-api, or it will 404 on direct load / refresh. */}
      <Routes>
        <Route path="/" element={
          <AuthGuard>
            <Index />
          </AuthGuard>
        } />
        {/* Meta paid-traffic landing. Also add /start to CloudFront SPA allowlist in caloriescience-api. */}
        <Route path="/start" element={<StartPage />} />
        <Route path="/signup" element={
          <AuthGuard>
            <SignUp />
          </AuthGuard>
        } />
        <Route path={SIGNUP_COMPLETION_PATH} element={
          <AuthGuard>
            <SignUp />
          </AuthGuard>
        } />
        <Route path="/login" element={
          <AuthGuard>
            <Login />
          </AuthGuard>
        } />
        <Route path="/forgot-password" element={
          <AuthGuard>
            <ForgotPassword />
          </AuthGuard>
        } />
        <Route path="/auth/consent-required" element={<ConsentRequiredPage />} />
        <Route path="/auth/subscription-required" element={<SubscriptionRequiredLegacyRedirect />} />
        <Route path="/onboarding/practice-type" element={
          <AuthGuard>
            {/* Practice-type selection is skipped for now; go straight to setup. */}
            <Navigate to="/onboarding/practice-type/institution" replace />
          </AuthGuard>
        } />
        <Route path="/onboarding/practice-type/individual" element={
          <AuthGuard>
            <IndividualOnboardingPage />
          </AuthGuard>
        } />
        <Route path="/onboarding/practice-type/institution" element={
          <AuthGuard>
            <InstitutionOnboardingPage />
          </AuthGuard>
        } />
        
        {/* Auth Callback Route */}
        <Route path="/auth/callback" element={
          <DashboardLayout>
            <CallbackPage />
          </DashboardLayout>
        } />
        {/* Auth onboarding welcome route for Google OAuth redirects */}
        <Route path="/auth/onboarding-welcome" element={<OnboardingWelcomePage />} />
        <Route path="/client-consent" element={<ClientConsentPage />} />
        <Route path="/about" element={<Navigate to="/#mission" replace />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/companion-app" element={<CompanionAppPage />} />
        <Route path="/platform/biomarker-informed-nutrition" element={<BiomarkerNutritionPage />} />
        <Route path="/personalised-nutrition-platform" element={<PersonalisedNutritionPage />} />
        <Route path="/terms" element={<LegalDocumentPage legalDocument={legalDocuments.websiteTerms} />} />
        <Route path="/platform-terms" element={<LegalDocumentPage legalDocument={legalDocuments.platformTerms} />} />
        <Route path="/founding-circle-agreement" element={<LegalDocumentPage legalDocument={legalDocuments.foundingCircleAgreement} />} />
        <Route path="/data-processing-agreement" element={<LegalDocumentPage legalDocument={legalDocuments.dataProcessingAgreement} />} />
        <Route path="/practitioner-declaration-consent" element={<LegalDocumentPage legalDocument={legalDocuments.practitionerDeclarationConsent} />} />
        <Route path="/privacy" element={<LegalDocumentPage legalDocument={legalDocuments.websitePrivacy} />} />
        <Route path="/platform-privacy" element={<LegalDocumentPage legalDocument={legalDocuments.platformPrivacy} />} />
        <Route path="/cookies" element={<LegalDocumentPage legalDocument={legalDocuments.cookies} />} />
        
        {/* Dashboard home page */}
        <Route path="/dashboard" element={
          <DashboardPage>
            <DashboardHome />
          </DashboardPage>
        } />
        {/* Backwards compatibility for old /dashboard/home URL */}
        <Route path="/dashboard/home" element={<Navigate to="/dashboard" replace />} />

        <Route path="/notifications" element={
          <DashboardPage>
            <NotificationsPage />
          </DashboardPage>
        } />

        {/* Appointments route */}
        <Route path="/appointments" element={
          <DashboardPage>
            <AvailabilityOnboardingGate>
              <AppointmentsPage />
            </AvailabilityOnboardingGate>
          </DashboardPage>
        } />

        <Route path="/appointments/calendar" element={
          <DashboardPage>
            <AvailabilityOnboardingGate>
              <AppointmentsCalendarViewPage />
            </AvailabilityOnboardingGate>
          </DashboardPage>
        } />
        
        <Route path="/clients" element={
          <DashboardPage>
            <ClientsListPage />
          </DashboardPage>
        } />

        <Route path="/clients/add" element={
          <DashboardPage>
            <AddClientPage />
          </DashboardPage>
        } />

        {/* New Client Details route */}
        <Route path="/clients/:id/details-v2/*" element={
          <DashboardPage>
            <ClientDetailsV2Page />
          </DashboardPage>
        } />

        <Route path="/clients/:id" element={
          <DashboardPage>
            <ClientDetailsPage />
          </DashboardPage>
        } />

        {/* Client Nutritional Targets route */}
        <Route path="/clients/:id/nutritional-targets" element={
          <DashboardPage>
            <NutritionalTargetsPage />
          </DashboardPage>
        } />


        <Route path="/meal-planning" element={
          <DashboardPage>
            <MealPlanningOptionsPage />
          </DashboardPage>
        } />

        <Route path="/generic-meal-plans" element={
          <DashboardPage>
            <GenericMealPlansPage />
          </DashboardPage>
        } />

        <Route path="/meal-planning/generic/:planId/nutrition/:dayNumber" element={
          <DashboardPage>
            <MealPlanningGenericMealPlanPage />
          </DashboardPage>
        } />

        <Route path="/meal-planning/client/:id/meal-plan" element={
          <DashboardPage>
            <MealPlanningClientMealPlanPage />
          </DashboardPage>
        } />

        <Route path="/ai-meal-planning" element={
          <DashboardPage>
            <CreateAIMealPlanPage />
          </DashboardPage>
        } />

        {/* Manual Meal Planning Routes */}
        <Route path="/manual-meal-plan" element={
          <DashboardPage>
            <CreateManualMealPlanPage />
          </DashboardPage>
        } />

        <Route path="/auto-meal-planning" element={
          <DashboardPage>
            <CreateAutoMealPlanPage />
          </DashboardPage>
        } />


        <Route path="/manual-meal-plan/:draftId/client/:clientId" element={
          <DashboardPage>
            <ManualMealPlanDraftPage />
          </DashboardPage>
        } />



        {/* AI Meal Plan Draft route */}
        <Route path="/ai-meal-plan/draft/:draftId/client/:clientId" element={
          <DashboardPage>
            <AIMealPlanDraftPage />
          </DashboardPage>
        } />

        <Route path="/ai-meal-plan/draft/:draftId/client/:clientId/recipe/:recipeId" element={
          <DashboardPage>
            <AIMealPlanRecipeDetailsPage />
          </DashboardPage>
        } />

        {/* Meal Plan Display route */}
        <Route path="/automated-meal-plan/draft/:draftId/client/:clientId" element={
          <DashboardPage>
            <AutomatedMealDraftPage />
          </DashboardPage>
        } />

        {/* Finalized Meal Plan route */}
        <Route path="/finalized-meal-plan/:planId/client/:clientId" element={
          <DashboardPage>
            <FinalizedMealPlanPage />
          </DashboardPage>
        } />

        {/* Nutritional Analysis route */}
        <Route path="/meal-plans/generic/:planId/nutrition/:dayNumber" element={
          <DashboardPage>
            <GenericMealPlanNutritionRoutePage />
          </DashboardPage>
        } />

        <Route path="/meal-plans/:planId/clients/:clientId/nutrition/:dayNumber" element={
          <DashboardPage>
            <MealPlanNutritionRoute />
          </DashboardPage>
        } />

        {/* Recipes route - moved up to avoid conflicts */}
        <Route path="/recipes" element={
          <DashboardPage>
            <RecipesPage />
          </DashboardPage>
        } />

        <Route path="/ingredients" element={
          <DashboardPage>
            <IngredientsPage />
          </DashboardPage>
        } />
        
        <Route path="/recipes/:id" element={
          <DashboardPage>
            <RecipeDetailsPage />
          </DashboardPage>
        } />

        {/* Recipe edit route */}
        <Route path="/recipes/:id/edit" element={
          <DashboardPage>
            <EditCustomRecipePage />
          </DashboardPage>
        } />


        
        <Route path="/recipes/create" element={
          <DashboardPage>
            <CreateCustomRecipePage />
          </DashboardPage>
        } />
        

      
        
        <Route path="/templates" element={
          <DashboardPage>
            <TemplatesPage />
          </DashboardPage>
        } />
        <Route path="/templates/:templateId/view" element={
          <DashboardPage>
            <TemplateViewRedirect />
          </DashboardPage>
        } />
        
        {/* Questionnaires routes */}
        <Route path="/questionnaires" element={
          <DashboardPage>
            <QuestionnairesFinalPage />
          </DashboardPage>
        } />
        <Route path="/questionnaires/:questionnaireId" element={
          <DashboardPage>
            <QuestionnaireFinalDetailPage />
          </DashboardPage>
        } />
        <Route path="/questionnaires/:questionnaireId/assign" element={
          <DashboardPage>
            <QuestionnaireAssignmentPage />
          </DashboardPage>
        } />
        <Route path="/questionnaires/:questionnaireId/responses" element={
          <DashboardPage>
            <QuestionnaireResponsesPage />
          </DashboardPage>
        } />
        <Route path="/questionnaires/:questionnaireId/responses/:instanceId" element={
          <DashboardPage>
            <QuestionnaireResponsePreviewPage />
          </DashboardPage>
        } />
        {/* Public client questionnaire route (no auth) */}
        <Route path="/questionnaire/share/:shareId" element={<PublicQuestionnairePage />} />
        {/* Public booking route (no auth) */}
        <Route path="/book/:linkId" element={<PublicBookingPage />} />
        <Route path="/reschedule/:linkId" element={<PublicReschedulePage />} />
        
        <Route path="/invoicing" element={
          <DashboardPage>
            <InvoicingPage />
          </DashboardPage>
        } />
        
        <Route path="/knowledgebase" element={
          <DashboardPage>
            <KnowledgebasePage />
          </DashboardPage>
        } />
        
        <Route path="/profile" element={
          <DashboardPage>
            <MyProfilePage />
          </DashboardPage>
        } />
        <Route path="/feedback" element={
          <DashboardPage>
            <FeedbackPage />
          </DashboardPage>
        } />
        <Route path="/admin/users" element={
          <DashboardPage>
            <UserProvisioningPage />
          </DashboardPage>
        } />
        <Route path="/admin/usage-quality" element={
          <DashboardPage>
            <UsageQualityMetricsPage />
          </DashboardPage>
        } />
        <Route path="/admin/landing-operations" element={
          <DashboardPage>
            <LandingOperationsPage />
          </DashboardPage>
        } />
        <Route path="/admin/client-invites" element={
          <DashboardPage>
            <AddClientPage adminInviteMode />
          </DashboardPage>
        } />
        <Route path="/admin/custom-recipes" element={
          <DashboardPage>
            <CustomRecipeReviewPage />
          </DashboardPage>
        } />
        <Route path="/admin/ai-recipes" element={
          <DashboardPage>
            <AIRecipeReviewPage />
          </DashboardPage>
        } />
        <Route path="/admin/feedback" element={
          <DashboardPage>
            <NutritionistFeedbackPage />
          </DashboardPage>
        } />
        
        {/* Catch-all route - must be last */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
